// ════════════════════════════════════════════════════════════════════════════
// components/CRM/DetalleCotizacion.jsx
// CSS inline SOLO para impresión, estilos normales en pantalla
// ════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate,useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getCotizacionById } from '../../services/crmService';
import api from '../../services/api';
import '../../styles/DetalleCotizacion.css';
import { useFiscal } from '../../hooks/useFiscal';

const DetalleCotizacion = ({ oportunidadId, onClose }) => {
  const fiscal = useFiscal();
  const { id: urlId } = useParams();
  const navigate = useNavigate();
  const efectivoId = oportunidadId || urlId;
  const esModal = !!oportunidadId;
  const location = useLocation();
  
   const datosDesdeState = location.state;

  const [cotizacion, setCotizacion] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // ═══════════════════════════════════════════════════════════════════════
  // Inyectar CSS de impresión
  // ═══════════════════════════════════════════════════════════════════════
  
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = printStyles;
    document.head.appendChild(styleElement);
    return () => {
      if (document.head.contains(styleElement)) {
        document.head.removeChild(styleElement);
      }
    };
  }, []);

  useEffect(() => {
    if (efectivoId) {
      cargarDatos();
    }
  }, [efectivoId]);

  // 🛠️ ESTABILIZACIÓN DE DATOS: Normalizamos partidas y totales
  const partidasParaMostrar = React.useMemo(() => {
    // PRIORIDAD 1: Si vienen datos del state (desde Pipeline)
    if (datosDesdeState?.propuestaData) {
      const propuesta = datosDesdeState.propuestaData;
      
      // Buscar partidas O lineas (tu fix original)
      const lista = propuesta.partidas || propuesta.lineas || [];
      
      if (Array.isArray(lista) && lista.length > 0) {
        console.log('✅ Usando partidas/lineas del state:', lista.length, 'items');
        return lista;
      }
    }
    
    // PRIORIDAD 2: Si no, buscar en la cotización cargada de la API
    if (!cotizacion) return [];
    
    const fuente = cotizacion.propuesta || cotizacion;
    let lista = fuente.lineas || fuente.partidas || [];

    if (typeof lista === 'string') {
      try { 
        lista = JSON.parse(lista); 
      } catch (e) { 
        lista = []; 
      }
    }

    return Array.isArray(lista) ? lista : [];
  }, [cotizacion, datosDesdeState]);

 const cargarDatos = async () => {
  try {
    setLoading(true);
    
    // Si tenemos datos del state, construir cotización desde ahí
    if (datosDesdeState?.propuestaData) {  // ← Verifica propuestaData
      console.log("✅ Usando datos del state:", datosDesdeState);
      
      const propuesta = datosDesdeState.propuestaData;  // ← Usa propuestaData (no propuestaOriginal)
      
      setCotizacion({
        id: efectivoId,
        numero_cotizacion: `CRM-${efectivoId}`,
        cliente: datosDesdeState.prospectoData || { nombre: 'Cliente' },  // ← prospectoData (no cliente)
        propuesta: propuesta,
        subtotal: propuesta.subtotal,
        itbis: propuesta.itbis || (propuesta.subtotal * 0.18),
        monto_total: propuesta.monto_total || propuesta.total,
        fecha_emision: new Date(),
        fecha_vencimiento: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        validez_dias: 30,
        observaciones: propuesta.observaciones || ''
      });
      
      const resConfig = await api.get('/configuracion');
      setConfig(resConfig.data);
      setLoading(false);
      return;
    }
    
    // Si no hay state, cargar de la API como antes
    const [resCotizacion, resConfig] = await Promise.all([
      getCotizacionById(efectivoId),
      api.get('/configuracion'),
    ]);
    
    setCotizacion(resCotizacion.data?.data || resCotizacion.data);
    setConfig(resConfig.data);
  } catch (err) {
    console.error('Error cargando cotización:', err);
    toast.error('No se pudo cargar la cotización');
  } finally {
    setLoading(false);
  }
};


  const handlePrint = () => {
    window.print();
  };

  const handleVolver = () => {
    if (esModal && onClose) {
      onClose();
    } else {
      navigate('/cotizaciones');
    }
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    return new Date(fecha).toLocaleDateString('es-DO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatearMonto = (monto) => {
    return `$${parseFloat(monto || 0).toLocaleString('es-DO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <p>Cargando cotización...</p>
      </div>
    );
  }

  if (!cotizacion) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <p>Cotización no encontrada</p>
        <button onClick={handleVolver}>Volver</button>
      </div>
    );
  }

  const estaVencida = cotizacion.fecha_vencimiento && new Date(cotizacion.fecha_vencimiento) < new Date();

  return (
    <div className="detalle-container" id="printArea">
      {/* VERSIÓN DE PANTALLA - Con botones */}
      <div className="detalle-header no-print">
        <h2>📄 Cotización {cotizacion.numero_cotizacion}</h2>
        <div className="detalle-actions">
          <button className="btn btn-secondary" onClick={handleVolver}>
            ← Volver
          </button>
          <button className="btn btn-primary" onClick={handlePrint}>
            🖨️ Imprimir / Guardar como PDF
          </button>
        </div>
      </div>

      {/* VERSIÓN DE IMPRESIÓN - Formato profesional */}
      <div className="print-only">
        {/* Header con logo y datos de empresa */}
        <div className="print-header">
          <div className="print-empresa">
            <h1>{config?.empresa_nombre || 'Mi Empresa'}</h1>
            <p><strong>RNC:</strong> {config?.empresa_rnc || 'N/A'}</p>
            <p><strong>Tel:</strong> {config?.empresa_telefono || 'N/A'}</p>
            <p><strong>Email:</strong> {config?.empresa_email || 'N/A'}</p>
            <p>{config?.empresa_direccion || ''}</p>
          </div>
          <div className="print-factura-info">
            <h2 className="print-factura-numero">COTIZACIÓN</h2>
            <p style={{ fontSize: '20px', fontWeight: 'bold', margin: '5px 0' }}>
              {cotizacion.numero_cotizacion}
            </p>
            <span 
              className="print-factura-estado"
              style={{ 
                background: estaVencida ? '#ef4444' : '#10b981',
                color: 'white',
                padding: '5px 10px',
                borderRadius: '5px',
                fontSize: '12px'
              }}
            >
              {estaVencida ? 'VENCIDA' : 'VIGENTE'}
            </span>
          </div>
        </div>

        {/* Cliente y fechas */}
        <div className="print-grid">
          <div className="print-section">
            <h3>COTIZACIÓN PARA:</h3>
            <p><strong>Cliente:</strong> {cotizacion.cliente?.nombre}</p>
            {cotizacion.cliente?.empresa && (
              <p><strong>Empresa:</strong> {cotizacion.cliente.empresa}</p>
            )}
            {cotizacion.cliente?.rnc_cedula && (
              <p><strong>{fiscal.id_fiscal_empresa_label || 'RNC'}/{fiscal.id_fiscal_persona_label || 'Cédula'}:</strong> {cotizacion.cliente.rnc_cedula}</p>
            )}
            {cotizacion.cliente?.telefono && (
              <p><strong>Teléfono:</strong> {cotizacion.cliente.telefono}</p>
            )}
            {cotizacion.cliente?.email && (
              <p><strong>Email:</strong> {cotizacion.cliente.email}</p>
            )}
            {cotizacion.cliente?.direccion && (
              <p><strong>Dirección:</strong> {cotizacion.cliente.direccion}</p>
            )}
          </div>

          <div className="print-section">
            <h3>INFORMACIÓN:</h3>
            <p><strong>Fecha de Emisión:</strong> {formatearFecha(cotizacion.fecha_emision)}</p>
            <p><strong>Fecha de Vencimiento:</strong> {formatearFecha(cotizacion.fecha_vencimiento)}</p>
            <p><strong>Validez:</strong> {cotizacion.validez_dias} días</p>
            {cotizacion.responsable?.nombre && (
              <p><strong>Responsable:</strong> {cotizacion.responsable.nombre}</p>
            )}
          </div>
        </div>

        {/* Tabla de partidas */}
        <table className="print-items-table">
          <thead>
            <tr>
              <th style={{ width: '5%' }}>#</th>
              <th style={{ width: '45%' }}>Descripción</th>
              <th style={{ width: '10%', textAlign: 'center' }}>Unidad</th>
              <th style={{ width: '10%', textAlign: 'right' }}>Cantidad</th>
              <th style={{ width: '15%', textAlign: 'right' }}>Precio Unit.</th>
              <th style={{ width: '15%', textAlign: 'right' }}>Total</th>
            </tr>
          </thead>
          <tbody className="print-table-body">
            {partidasParaMostrar.length > 0 ? (
              partidasParaMostrar.map((item, index) => (
                <tr key={index}>
                  <td>{item.codigo || index + 1}</td>
                  <td>{item.nombre || item.descripcion}</td>
                  <td>{item.unidad || 'und'}</td>
                  <td className="text-right">{Number(item.cantidad || 0).toLocaleString()}</td>
                  <td className="text-right">RD$ {Number(item.precio_unitario || item.precio || 0).toLocaleString()}</td>
                  <td className="text-right">RD$ {Number(item.total || 0).toLocaleString()}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" style={{textAlign: 'center', padding: '20px'}}>
                  No hay partidas para mostrar en esta cotización.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Totales */}
<div className="print-totales">
  <div className="print-totales-row">
    <span>Subtotal:</span>
    <span>{formatearMonto(cotizacion.subtotal)}</span>
  </div>
  
  <div className="print-totales-row">
    <span>{`${fiscal.impuesto_label || 'ITBIS'} (${fiscal.impuesto_tasa ?? 18}%):`}</span>
    <span>{formatearMonto(cotizacion.itbis)}</span>
  </div>

  <div className="print-totales-row total">
    <span><strong>TOTAL:</strong></span>
    <span><strong>{formatearMonto(cotizacion.monto_total)}</strong></span>
  </div>
</div>

        {/* Observaciones */}
        {cotizacion.observaciones && (
          <div className="print-condiciones">
            <h4>TÉRMINOS Y CONDICIONES:</h4>
            <p style={{ whiteSpace: 'pre-wrap' }}>{cotizacion.observaciones}</p>
          </div>
        )}

        {/* Notas al pie */}
        <div className="print-notas">
          <p><strong>NOTA:</strong> Esta cotización es válida por {cotizacion.validez_dias} días a partir de la fecha de emisión.</p>
          <p>{`Los precios incluyen ${fiscal.impuesto_label || 'ITBIS'} (${fiscal.impuesto_tasa ?? 18}%). Sujeto a disponibilidad.`}</p>
        </div>

        {/* Footer */}
        <div className="print-footer">
          <p>Gracias por su preferencia</p>
          <p style={{ fontSize: '10px', color: '#666' }}>
            Cotización generada el {formatearFecha(new Date())}
          </p>
        </div>
      </div>
    </div>
  );
};

const printStyles = `
  @media print {
    @page {
      margin: 10mm 12mm;
      size: letter;
    }
    
    body {
      margin: 0;
      padding: 0;
    }
    
    body * {
      visibility: hidden !important;
    }
    
    #printArea, #printArea * {
      visibility: visible !important;
    }
    
    #printArea {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      background: white !important;
      padding: 0 !important;
    }
    
    .no-print {
      display: none !important;
    }
    
    .print-only {
      display: block !important;
    }
    
    .print-header {
      display: flex;
      justify-content: space-between;
      margin-bottom: 15px;
      padding-bottom: 12px;
      border-bottom: 2px solid #0066cc;
    }
    
    .print-empresa h1 {
      font-size: 18px;
      color: #0066cc;
      margin: 0 0 3px 0;
    }
    
    .print-empresa p {
      margin: 2px 0;
      font-size: 9px;
      line-height: 1.3;
    }
    
    .print-factura-numero {
      font-size: 18px;
      color: #0066cc;
      margin: 0;
    }
    
    .print-factura-info p {
      font-size: 9px;
      margin: 2px 0;
    }
    
    .print-factura-estado {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .print-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
      margin-bottom: 15px;
    }
    
    .print-section {
      border: 1px solid #ddd;
      padding: 8px;
      border-radius: 4px;
    }
    
    .print-section h3 {
      font-size: 11px;
      color: #0066cc;
      margin: 0 0 5px 0;
      border-bottom: 1px solid #f0f0f0;
      padding-bottom: 3px;
    }
    
    .print-section p {
      margin: 2px 0;
      font-size: 9px;
      line-height: 1.3;
    }
    
    .print-items-table {
      width: 100%;
      border-collapse: collapse;
      margin: 10px 0;
      font-size: 9px;
    }
    
    .print-items-table thead {
      background: #0066cc !important;
      color: white !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .print-items-table th {
      padding: 5px 4px;
      font-size: 9px;
    }
    
    .print-items-table td {
      padding: 4px;
      border-bottom: 1px solid #e0e0e0;
      font-size: 9px;
    }
    
    .print-totales {
      width: 250px;
      margin-left: auto;
      margin-top: 10px;
      border: 2px solid #0066cc;
      padding: 8px;
      border-radius: 4px;
    }
    
    .print-totales-row {
      display: flex;
      justify-content: space-between;
      padding: 3px 0;
      font-size: 9px;
    }
    
    .print-totales-row.total {
      border-top: 2px solid #0066cc;
      margin-top: 5px;
      padding-top: 8px;
      font-size: 13px;
      font-weight: bold;
      color: #0066cc;
    }
    
    .print-condiciones {
      margin-top: 10px;
      padding: 8px;
      background: #f9fafb !important;
      border-left: 4px solid #0066cc;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .print-condiciones h4 {
      font-size: 11px;
      margin: 0 0 5px 0;
    }
    
    .print-condiciones p {
      font-size: 9px;
      margin: 0;
    }
    
    .print-notas {
      margin-top: 10px;
      padding: 8px;
      background: #fef3c7 !important;
      border-left: 4px solid #f59e0b;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .print-notas p {
      font-size: 8px;
      margin: 3px 0;
    }
    
    .print-footer {
      margin-top: 15px;
      padding-top: 8px;
      border-top: 1px solid #e0e0e0;
      font-size: 7px;
      color: #666;
      text-align: center;
    }
    
    .print-footer p {
      margin: 2px 0;
    }
  }
`;

export default DetalleCotizacion;